/**
 * Modal para mostrar el proceso manual de consulta y actualización de guías de Envía Colvanes
 */
import React, { useEffect } from 'react'
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  ClockIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'
import { useColvanesManualProcess } from '../../hooks/cron/useColvanesManualProcess'

interface ColvanesManualProcessModalProps {
  isOpen: boolean
  onClose: () => void
}

const ColvanesManualProcessModal: React.FC<ColvanesManualProcessModalProps> = ({
  isOpen,
  onClose,
}) => {
  const {
    isProcessing,
    currentPhase,
    progress,
    results,
    error,
    execute,
    reset,
  } = useColvanesManualProcess()

  // Ejecutar automáticamente al abrir el modal
  useEffect(() => {
    if (isOpen && currentPhase === 'idle' && !isProcessing) {
      execute()
    }
  }, [isOpen, currentPhase, isProcessing, execute])

  // Resetear al cerrar
  const handleClose = () => {
    if (!isProcessing) {
      reset()
      onClose()
    }
  }

  if (!isOpen) return null

  // Calcular porcentajes de progreso
  const ordersProgress =
    progress.totalPages > 0
      ? Math.round((progress.currentPage / progress.totalPages) * 100)
      : 0

  const guidesProgress =
    progress.totalGuides > 0
      ? Math.round((progress.guidesProcessed / progress.totalGuides) * 100)
      : 0

  // Función para obtener el icono de la fase actual
  const getPhaseIcon = () => {
    switch (currentPhase) {
      case 'fetching_orders':
        return <ArrowPathIcon className="h-8 w-8 text-blue-500 animate-spin" />
      case 'processing_guides':
        return <ArrowPathIcon className="h-8 w-8 text-blue-500 animate-spin" />
      case 'completed':
        return <CheckCircleIcon className="h-8 w-8 text-green-500" />
      case 'error':
        return <ExclamationTriangleIcon className="h-8 w-8 text-red-500" />
      default:
        return <ClockIcon className="h-8 w-8 text-gray-400" />
    }
  }

  // Función para obtener el texto de la fase actual
  const getPhaseText = () => {
    switch (currentPhase) {
      case 'fetching_orders':
        return 'Consultando pedidos...'
      case 'processing_guides':
        return 'Procesando guías...'
      case 'completed':
        return 'Proceso completado'
      case 'error':
        return 'Error en el proceso'
      default:
        return 'Preparando...'
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 m-4 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h3>
            Proceso Manual de Envía Colvanes
          </h3>
          <button
            onClick={handleClose}
            disabled={isProcessing}
            className={`text-gray-500 hover:text-gray-700 ${
              isProcessing ? 'cursor-not-allowed opacity-50' : ''
            }`}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Estado actual */}
        <div className="flex items-center gap-3 mb-6">
          {getPhaseIcon()}
          <span className="text-lg font-medium">{getPhaseText()}</span>
        </div>

        {/* Fase 1: Consultando pedidos */}
        {currentPhase === 'fetching_orders' && (
          <div className="space-y-4 mb-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  Consultando páginas de pedidos...
                </span>
                <span className="text-sm text-gray-500">
                  {progress.currentPage} de {progress.totalPages} páginas
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${ordersProgress}%` }}
                />
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-blue-800">
                    Pedidos encontrados:
                  </span>
                  <span className="ml-2 text-blue-700">
                    {progress.ordersFetched}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-blue-800">
                    Guías extraídas:
                  </span>
                  <span className="ml-2 text-blue-700">
                    {progress.guidesExtracted}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Fase 2: Procesando guías */}
        {currentPhase === 'processing_guides' && (
          <div className="space-y-4 mb-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  Procesando guías individualmente...
                </span>
                <span className="text-sm text-gray-500">
                  Guía {progress.currentGuide} de {progress.totalGuides} (
                  {guidesProgress}%)
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${guidesProgress}%` }}
                />
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-blue-800">
                    Guías procesadas:
                  </span>
                  <span className="ml-2 text-blue-700">
                    {progress.guidesProcessed} de {progress.totalGuides}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-blue-800">Guía actual:</span>
                  <span className="ml-2 text-blue-700">
                    {progress.currentGuide} de {progress.totalGuides}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <span className="text-sm font-medium text-red-800 block mb-1">
                  Error en el Proceso
                </span>
                <span className="text-sm text-red-700">{error}</span>
              </div>
            </div>
          </div>
        )}

        {/* Fase 3: Resultados */}
        {currentPhase === 'completed' && (
          <div className="space-y-6">
            {/* Resumen de resultados */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="text-2xl font-bold text-blue-600">
                  {results.summary.totalGuides}
                </div>
                <div className="text-sm text-blue-700">Total Procesadas</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="text-2xl font-bold text-green-600">
                  {results.summary.updated_count}
                </div>
                <div className="text-sm text-green-700">Actualizadas</div>
              </div>
              <div className="text-center p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <div className="text-2xl font-bold text-yellow-600">
                  {results.summary.not_delivered_count +
                    results.summary.not_found_count +
                    results.summary.skipped_count}
                </div>
                <div className="text-sm text-yellow-700">No Actualizadas</div>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg border border-red-200">
                <div className="text-2xl font-bold text-red-600">
                  {results.summary.error_count}
                </div>
                <div className="text-sm text-red-700">Errores</div>
              </div>
            </div>

            {/* Lista de pedidos actualizados */}
            {results.updated.length > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-green-800 mb-3">
                  Pedidos Actualizados ({results.updated.length})
                </h4>
                <div className="max-h-48 overflow-y-auto">
                  <div className="space-y-2">
                    {results.updated.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between bg-white rounded p-2 text-sm"
                      >
                        <div>
                          <span className="font-medium text-gray-900">
                            Pedido:
                          </span>
                          <span className="ml-2 text-gray-700">
                            {item.id ?? item.orders_id}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-900">
                            Guía:
                          </span>
                          <span className="ml-2 text-gray-700">{item.guia}</span>
                        </div>
                        {item.previous_status && item.new_status && (
                          <div className="text-xs text-gray-600">
                            <span className="font-medium">
                              {item.previous_status}
                            </span>
                            <span className="mx-1">→</span>
                            <span className="font-medium text-green-600">
                              {item.new_status}
                            </span>
                          </div>
                        )}
                        {item.store_name && (
                          <div>
                            <span className="text-xs text-gray-500">
                              {item.store_name}
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Lista de guías no actualizadas */}
            {results.notUpdated.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-yellow-800 mb-3">
                  Guías No Actualizadas ({results.notUpdated.length})
                </h4>
                <div className="max-h-48 overflow-y-auto">
                  <div className="space-y-2">
                    {results.notUpdated.slice(0, 20).map((item, index) => (
                      <div
                        key={index}
                        className="bg-white rounded p-2 text-sm"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div>
                            <span className="font-medium text-gray-900">
                              Pedido:
                            </span>
                            <span className="ml-2 text-gray-700">
                              {item.id ?? item.orders_id}
                            </span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-900">
                              Guía:
                            </span>
                            <span className="ml-2 text-gray-700">
                              {item.guia}
                            </span>
                          </div>
                        </div>
                        <div className="text-xs text-yellow-700 mt-1">
                          <span className="font-medium">Razón:</span>{' '}
                          {item.reason}
                        </div>
                        {item.estado_guia && (
                          <div className="text-xs text-gray-600 mt-1">
                            <span className="font-medium">Estado guía:</span>{' '}
                            {item.estado_guia}
                          </div>
                        )}
                      </div>
                    ))}
                    {results.notUpdated.length > 20 && (
                      <div className="text-xs text-yellow-600 italic text-center mt-2">
                        ... y {results.notUpdated.length - 20} más
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Mensaje si no hay resultados */}
            {results.updated.length === 0 &&
              results.notUpdated.length === 0 && (
                <div className="text-center py-8">
                  <ExclamationTriangleIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-lg text-gray-600">
                    No se encontraron guías para procesar
                  </p>
                </div>
              )}
          </div>
        )}

        {/* Botones de acción */}
        <div className="flex justify-end gap-3 mt-6">
          {currentPhase === 'completed' && (
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Cerrar
            </button>
          )}

          {currentPhase === 'error' && (
            <>
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cerrar
              </button>
              <button
                onClick={execute}
                disabled={isProcessing}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                Reintentar
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default ColvanesManualProcessModal

